import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuth } from '../contexts/AuthContext';
import { Bell, Plus, X, Loader2, MessageSquare, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function ChecklistSettingsPage() {
  const { user } = useAuth();
  const [checklistSettings, setChecklistSettings] = useState({
    notification_times: ["12:00", "15:30"],
    notification_recipients: ["admin", "assigned_nurse"],
    sms_enabled: true,
    email_enabled: true
  });
  const [loading, setLoading] = useState(false);
  const [newNotificationTime, setNewNotificationTime] = useState("");
  const [smsFooter, setSmsFooter] = useState('');
  const [footerLoading, setFooterLoading] = useState(false);
  
  // Appointment settings
  const [appointmentSettings, setAppointmentSettings] = useState({
    clinic_opening_time: "09:00 AM",
    clinic_closing_time: "05:00 PM",
    appointment_slot_duration: 15,
    break_start_time: "",
    break_end_time: ""
  });
  const [appointmentLoading, setAppointmentLoading] = useState(false);
  const [timeSlots, setTimeSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadChecklistSettings();
    loadClinicFooter();
    loadAppointmentSettings();
  }, []);

  const loadChecklistSettings = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/checklist/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setChecklistSettings(data);
      }
    } catch (error) {
      console.error("Failed to load checklist settings:", error);
    }
  };

  const saveChecklistSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/checklist/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(checklistSettings)
      });

      if (response.ok) {
        toast.success("Checklist notification settings saved");
      } else {
        toast.error("Failed to save settings");
      }
    } catch (error) {
      console.error("Failed to save checklist settings:", error);
      toast.error("Failed to save checklist settings");
    } finally {
      setLoading(false);
    }
  };

  const addNotificationTime = () => {
    if (newNotificationTime && !checklistSettings.notification_times.includes(newNotificationTime)) {
      setChecklistSettings({
        ...checklistSettings,
        notification_times: [...checklistSettings.notification_times, newNotificationTime]
      });
      setNewNotificationTime("");
    }
  };

  const removeNotificationTime = (time) => {
    setChecklistSettings({
      ...checklistSettings,
      notification_times: checklistSettings.notification_times.filter(t => t !== time)
    });
  };

  const toggleRecipient = (recipient) => {
    const recipients = checklistSettings.notification_recipients;
    if (recipients.includes(recipient)) {
      setChecklistSettings({
        ...checklistSettings,
        notification_recipients: recipients.filter(r => r !== recipient)
      });
    } else {
      setChecklistSettings({
        ...checklistSettings,
        notification_recipients: [...recipients, recipient]
      });
    }
  };

  const loadClinicFooter = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/sms/clinic-footer`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSmsFooter(data.sms_footer || '');
      }
    } catch (error) {
      console.error('Error loading clinic footer:', error);
    }
  };

  const handleUpdateFooter = async () => {
    setFooterLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/sms/clinic-footer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ sms_footer: smsFooter })
      });

      if (response.ok) {
        toast.success('✓ Clinic SMS footer updated successfully');
      } else {
        const data = await response.json();
        toast.error('✗ ' + (data.detail || 'Failed to update footer'));
      }
    } catch (error) {
      console.error('Error updating footer:', error);
      toast.error('✗ Failed to update footer');
    } finally {
      setFooterLoading(false);
    }
  };

  const loadAppointmentSettings = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/appointments/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAppointmentSettings({
          clinic_opening_time: data.clinic_opening_time || "09:00 AM",
          clinic_closing_time: data.clinic_closing_time || "05:00 PM",
          appointment_slot_duration: data.appointment_slot_duration || 15,
          break_start_time: data.break_start_time || "",
          break_end_time: data.break_end_time || ""
        });
      }
    } catch (error) {
      console.error('Error loading appointment settings:', error);
    }
  };

  const saveAppointmentSettings = async () => {
    setAppointmentLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/appointments/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(appointmentSettings)
      });

      if (response.ok) {
        toast.success('✓ Appointment settings saved successfully');
        // Refresh time slots after saving
        loadTimeSlots();
      } else {
        const data = await response.json();
        toast.error('✗ ' + (data.detail || 'Failed to save settings'));
      }
    } catch (error) {
      console.error('Error saving appointment settings:', error);
      toast.error('✗ Failed to save appointment settings');
    } finally {
      setAppointmentLoading(false);
    }
  };

  const loadTimeSlots = async () => {
    setLoadingSlots(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/appointments/time-slots`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTimeSlots(data.slots || []);
      } else {
        console.error('Failed to load time slots');
        setTimeSlots([]);
      }
    } catch (error) {
      console.error('Error loading time slots:', error);
      setTimeSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: "#652D90" }}>Clinic Settings</h1>
          <p className="text-gray-600 mt-2">
            Configure checklist notifications and SMS footer for your clinic.
            {user?.role === 'clinic_admin' && (
              <span className="font-semibold text-purple-600"> Your settings override system defaults.</span>
            )}
          </p>
        </div>

        <Card className="rounded-2xl border-[#e5e5e5]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: "#652D90" }}>
              <Bell size={20} />
              Notification Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Notification Times */}
            <div className="space-y-3">
              <Label style={{ color: "#652D90", fontWeight: 600 }}>Notification Times</Label>
              <p className="text-xs text-gray-500">
                Set times when you'll be notified if checklist has pending items
              </p>
              <div className="flex gap-2">
                <Input
                  type="time"
                  value={newNotificationTime}
                  onChange={(e) => setNewNotificationTime(e.target.value)}
                  className="rounded-xl"
                />
                <Button
                  onClick={addNotificationTime}
                  size="sm"
                  variant="outline"
                  className="rounded-full"
                >
                  <Plus size={16} />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {checklistSettings.notification_times.map((time) => (
                  <div
                    key={time}
                    className="flex items-center gap-1 bg-purple-100 px-3 py-1 rounded-full"
                  >
                    <span className="text-sm" style={{ color: "#652D90" }}>{time}</span>
                    <button
                      onClick={() => removeNotificationTime(time)}
                      className="hover:text-red-600"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Recipients */}
            <div className="space-y-3">
              <Label style={{ color: "#652D90", fontWeight: 600 }}>Notification Recipients</Label>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={checklistSettings.notification_recipients.includes("admin")}
                    onChange={() => toggleRecipient("admin")}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">Clinic Admin</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={checklistSettings.notification_recipients.includes("assigned_nurse")}
                    onChange={() => toggleRecipient("assigned_nurse")}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">Assigned Nurse</span>
                </label>
              </div>
            </div>

            {/* Notification Methods */}
            <div className="space-y-3">
              <Label style={{ color: "#652D90", fontWeight: 600 }}>Notification Methods</Label>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={checklistSettings.sms_enabled}
                    onChange={(e) => setChecklistSettings({...checklistSettings, sms_enabled: e.target.checked})}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">SMS Notification</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={checklistSettings.email_enabled}
                    onChange={(e) => setChecklistSettings({...checklistSettings, email_enabled: e.target.checked})}
                    className="h-4 w-4"
                  />
                  <span className="text-sm">Email Notification</span>
                </label>
              </div>
            </div>

            <Button
              onClick={saveChecklistSettings}
              disabled={loading}
              style={{ backgroundColor: "#652D90" }}
              className="w-full rounded-xl"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Notification Settings'
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Appointment Time Slot Settings */}
        <Card className="rounded-2xl border-[#e5e5e5]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: "#652D90" }}>
              <Clock size={20} />
              Appointment Time Slot Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Configure appointment time slots for your clinic. These settings control when patients can book appointments.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label style={{ color: "#652D90", fontWeight: 600 }}>Clinic Opening Time</Label>
                <Input
                  type="time"
                  value={appointmentSettings.clinic_opening_time.replace(' AM', '').replace(' PM', '')}
                  onChange={(e) => {
                    const time24 = e.target.value;
                    const [hours, minutes] = time24.split(':');
                    const hour = parseInt(hours);
                    const ampm = hour >= 12 ? 'PM' : 'AM';
                    const hour12 = hour % 12 || 12;
                    const time12 = `${String(hour12).padStart(2, '0')}:${minutes} ${ampm}`;
                    setAppointmentSettings({...appointmentSettings, clinic_opening_time: time12});
                  }}
                  className="rounded-xl"
                />
              </div>

              <div>
                <Label style={{ color: "#652D90", fontWeight: 600 }}>Clinic Closing Time</Label>
                <Input
                  type="time"
                  value={appointmentSettings.clinic_closing_time.replace(' AM', '').replace(' PM', '')}
                  onChange={(e) => {
                    const time24 = e.target.value;
                    const [hours, minutes] = time24.split(':');
                    const hour = parseInt(hours);
                    const ampm = hour >= 12 ? 'PM' : 'AM';
                    const hour12 = hour % 12 || 12;
                    const time12 = `${String(hour12).padStart(2, '0')}:${minutes} ${ampm}`;
                    setAppointmentSettings({...appointmentSettings, clinic_closing_time: time12});
                  }}
                  className="rounded-xl"
                />
              </div>
            </div>

            <div>
              <Label style={{ color: "#652D90", fontWeight: 600 }}>Appointment Slot Duration (minutes)</Label>
              <Input
                type="number"
                min="5"
                max="120"
                step="5"
                value={appointmentSettings.appointment_slot_duration}
                onChange={(e) => setAppointmentSettings({...appointmentSettings, appointment_slot_duration: parseInt(e.target.value)})}
                className="rounded-xl"
              />
              <p className="text-xs text-gray-500 mt-1">Duration for each appointment slot (5-120 minutes)</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label style={{ color: "#652D90", fontWeight: 600 }}>Break Start Time (Optional)</Label>
                <Input
                  type="time"
                  value={appointmentSettings.break_start_time ? appointmentSettings.break_start_time.replace(' AM', '').replace(' PM', '') : ''}
                  onChange={(e) => {
                    if (!e.target.value) {
                      setAppointmentSettings({...appointmentSettings, break_start_time: ''});
                      return;
                    }
                    const time24 = e.target.value;
                    const [hours, minutes] = time24.split(':');
                    const hour = parseInt(hours);
                    const ampm = hour >= 12 ? 'PM' : 'AM';
                    const hour12 = hour % 12 || 12;
                    const time12 = `${String(hour12).padStart(2, '0')}:${minutes} ${ampm}`;
                    setAppointmentSettings({...appointmentSettings, break_start_time: time12});
                  }}
                  className="rounded-xl"
                />
              </div>

              <div>
                <Label style={{ color: "#652D90", fontWeight: 600 }}>Break End Time (Optional)</Label>
                <Input
                  type="time"
                  value={appointmentSettings.break_end_time ? appointmentSettings.break_end_time.replace(' AM', '').replace(' PM', '') : ''}
                  onChange={(e) => {
                    if (!e.target.value) {
                      setAppointmentSettings({...appointmentSettings, break_end_time: ''});
                      return;
                    }
                    const time24 = e.target.value;
                    const [hours, minutes] = time24.split(':');
                    const hour = parseInt(hours);
                    const ampm = hour >= 12 ? 'PM' : 'AM';
                    const hour12 = hour % 12 || 12;
                    const time12 = `${String(hour12).padStart(2, '0')}:${minutes} ${ampm}`;
                    setAppointmentSettings({...appointmentSettings, break_end_time: time12});
                  }}
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={saveAppointmentSettings}
                disabled={appointmentLoading}
                className="rounded-xl flex-1"
                style={{ backgroundColor: "#652D90" }}
              >
                {appointmentLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Appointment Settings
              </Button>
              
              <Button
                onClick={loadTimeSlots}
                disabled={loadingSlots}
                variant="outline"
                className="rounded-xl"
                style={{ borderColor: "#652D90", color: "#652D90" }}
              >
                {loadingSlots && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Preview Slots
              </Button>
            </div>

            {/* Time Slots Preview */}
            {timeSlots.length > 0 && (
              <div className="border rounded-xl p-4 bg-gray-50">
                <h4 className="font-semibold mb-3" style={{ color: "#652D90" }}>
                  Generated Time Slots ({timeSlots.length} slots)
                </h4>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-64 overflow-y-auto">
                  {timeSlots.map((slot, idx) => (
                    <div
                      key={idx}
                      className="px-3 py-2 bg-white border border-purple-200 rounded-lg text-center text-sm"
                      style={{ color: "#652D90" }}
                    >
                      {slot}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Alert>
              <AlertDescription className="text-sm">
                <strong>Note:</strong> Time slots will be automatically generated based on these settings. Patients will be able to select available slots when booking appointments.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* SMS Footer Settings */}
        <Card className="rounded-2xl border-[#e5e5e5]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: "#652D90" }}>
              <MessageSquare size={20} />
              Clinic SMS Footer
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label style={{ color: "#652D90", fontWeight: 600 }}>SMS Footer Text</Label>
              <p className="text-xs text-gray-500 mb-2">
                This footer is automatically added to all patient SMS sent from your clinic
              </p>
              <Textarea
                value={smsFooter}
                onChange={(e) => setSmsFooter(e.target.value)}
                placeholder="e.g., Health Yaad Aayo Clinic&#10;Ph: 9851234567"
                rows={3}
                className="rounded-xl"
              />
              <p className="text-sm text-gray-500 mt-1">
                {smsFooter.length} characters
              </p>
            </div>

            <Button
              onClick={handleUpdateFooter}
              disabled={footerLoading}
              className="rounded-xl"
              style={{ backgroundColor: "#652D90" }}
            >
              {footerLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update SMS Footer
            </Button>

            <Alert>
              <AlertDescription className="text-sm">
                <strong>Note:</strong> The SMS footer is automatically added to all patient communications, including follow-up reminders and marketing SMS. You can edit it before sending marketing SMS if needed.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
